import { APITypesV1 } from "@cur8/api-client";
import { CheckinSessionState, fromAPI } from "@cur8/rich-entity";
import { skipToken, useQuery } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { DateTime } from "luxon";
import { useAPIClient } from "render/context/APIContext";
import { queryFn as visitQueryFn } from "./useVisitQuery";
import { useRef } from "react";

export function useVisitToCheckInQuery({
  checkInSession: checkInSession,
  origin,
}: {
  checkInSession?: CheckinSessionState;
  origin: DateTime;
}) {
  const wasVisitAssignedOnBackendRef = useRef<boolean>();
  if (wasVisitAssignedOnBackendRef.current === undefined && checkInSession) {
    wasVisitAssignedOnBackendRef.current = !!checkInSession.visitId;
  }
  const wasVisitAssignedOnBackend = wasVisitAssignedOnBackendRef.current;
  const apiClient = useAPIClient();
  const patientId = checkInSession?.patientId;
  const visitId = checkInSession?.visitId;

  const visitToCheckInQuery = useQuery({
    queryKey: queryKey(patientId),

    /**
     * 27537: Assign visit id to CheckinSession when a member joins the session
     *
     * This is a safety net for migration to the new behavior, when visit id
     * is assigned by APIBackend, when member joins the check-in session.
     *
     * In this scenario we want to fetch the already assigned visit rather than
     * searching for a new one.
     *
     * Once we have no old check-in sessions without site id, which still require
     * visits to be assigned by ConciergeUI, we can remove this logic. This is
     * expected to take a few hours after release, we can check this by looking for
     * the following log entries in App Insights
     * -----------------------------------------
     * A check-in session ${sessionId} has no assigned visitId after a member has joined the session. Assigning visit ${visit.visitId} on site ${visit.siteId} manually.
     */
    queryFn:
      checkInSession && patientId
        ? visitId && wasVisitAssignedOnBackend
          ? visitQueryFn(apiClient, patientId, visitId)
          : visitToCheckInQueryFn(apiClient, patientId, origin)
        : skipToken,
  });

  return { visitToCheckInQuery, wasVisitAssignedOnBackend };
}

function visitToCheckInQueryFn(
  apiClient: APIClient,
  patientId: string,
  origin: DateTime
) {
  return async function () {
    const range = {
      start: origin.startOf("day"),
      end: origin.endOf("day"),
    };

    const startTime = range.start.toISO();
    const endTime = range.end.toISO();
    if (!startTime || !endTime) {
      throw new Error("Invalid Dates");
    }

    const visitDTO = await apiClient.visit.getPatientVisits(
      {
        patientId,
      },
      {
        pageSize: 1,
        startDateTimeOffsetRange: {
          start: startTime,
          end: endTime,
        },
        status: [APITypesV1.VisitStatus.Scheduled],
      }
    ).result;

    return visitDTO.items.map(fromAPI.toVisit).at(0);
  };
}

function queryKey(patientId: string | undefined) {
  return ["visitToCheckIn", patientId] as const;
}
